import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { useCraneContext } from "../CraneContext";
import {
  CatCrane,
  CAT_CRANE_INITIAL_VALUES,
} from "@/pages/billets/models/CatCrane";
import {
  deleteCrane,
  upsertCrane,
} from "@/pages/billets/repositories/CraneRepository";

type onModalHideProps = {
  entityId?: string;
  shouldUpdate?: boolean;
};

type Props = {
  crane: CatCrane | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
  refreshData: Function;
};

export const CraneModal = ({
  crane,
  showModal,
  onHide,
  refreshData,
}: Props) => {
  const { state } = useCraneContext();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingCrane, setExistingCrane] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatCrane>({
    defaultValues: CAT_CRANE_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatCrane> = async (data: CatCrane) => {
    setIsSubmitLoading(true);
    const cranes: CatCrane[] = state.cranes || [];

    if (showModal === "delete") {
      const resp = await deleteCrane(crane?.CraneId ?? 0);
      if (resp.ok) {
        await refreshData();
        setNotificationMessage("The crane was deleted successfully");
        onHide({ shouldUpdate: true });
      } else {
        setError(resp.message);
        onHide({ shouldUpdate: false });
      }
      setIsSubmitLoading(false);
    } else {
      if (
        cranes.some((c) => c.Name === data.Name && c.CraneId !== crane?.CraneId)
      ) {
        setExistingCrane(data.Name || "");
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatCrane) => {
    const resp = await upsertCrane(data);
    if (resp.ok) {
      const entityId = get(
        resp,
        "data.tables[0].rows[0].CraneId",
        data.CraneId,
      );
      await refreshData();
      setNotificationMessage("The crane was saved successfully");
      onHide({ entityId: String(entityId), shouldUpdate: true });
    } else {
      setError(resp.message);
      onHide({ shouldUpdate: false });
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(crane || CAT_CRANE_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        label="Crane"
        existingName={existingCrane}
        onHide={(proceed: boolean) => {
          setConfirmOpen(false);
          if (proceed) {
            performUpsert(getValues());
          } else {
            setIsSubmitLoading(false);
          }
        }}
      />
      <MesfModal
        title={`${title} CRANE`}
        open={showModal !== ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="crane-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={8}>
                <Controller
                  name="Name"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: (value) =>
                      (value as string)?.trim() !== "" ||
                      "Name cannot be empty",
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value || ""}
                      onChange={field.onChange}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                      inputProps={{
                        maxLength: 50,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Crane"
      />
    </>
  );
};
